import {
	QueryClient,
	QueryKey,
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { APPLICATION_STATUS } from "../configs/constants/ApplicationStatus";
import { JOB_STATUS } from "../configs/constants/JobStatus";
import { FEEDBACK_MESSAGE_TYPE } from "../configs/feedbackMessages";
import { ApiErrorResponse } from "../models/ApiResponse";
import Job, {
	Application,
	CreateJobRequest,
	Invitation,
	JobScheduleResponse,
	JobsResponse,
	ScheduleTooltipData,
	Shift,
} from "../models/Job";
import { JobHistoryEntry } from "../models/JobHistory";
import { MatchingConsultant } from "../models/User";
import { isUndefinedOrTrue } from "../utils/object.utils";
import { QueryOpts } from "./api-hooks";
import { apiStore } from "../stores/api.store";
import { toast } from "../utils/toast";

export const queryClient = new QueryClient();

export type GetJobsParams = {
	page: number;
	pageSize: number;
	status?: JOB_STATUS[];
	openShiftsAt?: string[];
	bookedShiftsAt?: string[];
	anyShiftsAt?: string[];
	keyword?: string;
	startDate?: string | Date;
	endDate?: string | Date;
	pool?: string;
	/** comma-joined descriptive job tag keys (matches jobs carrying ALL of them) */
	tags?: string;
	/** comma-joined customer tag keys (matches jobs whose customer carries ANY of them) */
	customerTags?: string;
	/** comma-joined consultant tag keys the job must REQUIRE (requirements.tags, ALL of them) */
	requirementTags?: string;
	/** comma-joined certification type keys the job must REQUIRE (requirements.certifications, ALL of them) */
	requirementCertifications?: string;
	minDays?: number;
	minHours?: number;
	sort?: string;
	order?: string;
	expand?: string[];
	customerId?: string;
	contractId?: string;
	includeOverlappingRange?: boolean;
};

export function useJobs(query: GetJobsParams, opts: QueryOpts = {}) {
	const { data, ...rest } = useQuery(
		["jobs", query],
		() =>
			apiStore
				.get<JobsResponse>({
					path: "/job",
					isAuthRequired: true,
					query: {
						...query,
						status: query.status ? query.status.join(",") : undefined,
						startDate: query.startDate ? new Date(query.startDate).toISOString() : undefined,
						endDate: query.endDate ? new Date(query.endDate).toISOString() : undefined,
						expand: query.expand ? query.expand.join(",") : "customer,applications",
						openShiftsAt: query.openShiftsAt ? query.openShiftsAt.join(",") : undefined,
						bookedShiftsAt: query.bookedShiftsAt ? query.bookedShiftsAt.join(",") : undefined,
						anyShiftsAt: query.anyShiftsAt ? query.anyShiftsAt.join(",") : undefined,
					},
				})
				.then((d) => d.data),
		{
			keepPreviousData: true,
			...opts,
		}
	);

	return {
		jobList: data,
		...rest,
	};
}

/**
 * Infinite-scroll variant of {@link useJobs} for the virtualized jobs list.
 * Pages are fetched server-side; `getNextPageParam` derives the next page from
 * `totalCount` vs the number of jobs loaded so far. The query key includes the
 * resolved params, so changing filter/sort/keyword starts a fresh query (the
 * accumulated pages reset). Rows are flattened and de-duped by `job.id` to
 * absorb offset drift from concurrent inserts mid-scroll.
 *
 * No `refetchInterval` here on purpose: polling an infinite query refetches
 * *all* accumulated pages. Freshness comes from `refetchOnWindowFocus` and
 * mutation invalidation of the `["jobs"]` key instead.
 */
export function useJobsInfinite(query: Omit<GetJobsParams, "page">, opts: any = {}) {
	const infinite = useInfiniteQuery(
		["jobs", "infinite", query],
		({ pageParam = 1 }) =>
			apiStore
				.get<JobsResponse>({
					path: "/job",
					isAuthRequired: true,
					query: {
						...query,
						page: pageParam,
						status: query.status ? query.status.join(",") : undefined,
						startDate: query.startDate ? new Date(query.startDate).toISOString() : undefined,
						endDate: query.endDate ? new Date(query.endDate).toISOString() : undefined,
						expand: query.expand ? query.expand.join(",") : "customer,applications",
						openShiftsAt: query.openShiftsAt ? query.openShiftsAt.join(",") : undefined,
						bookedShiftsAt: query.bookedShiftsAt ? query.bookedShiftsAt.join(",") : undefined,
						anyShiftsAt: query.anyShiftsAt ? query.anyShiftsAt.join(",") : undefined,
					},
				})
				.then((d) => d.data),
		{
			getNextPageParam: (lastPage, allPages) => {
				const loaded = allPages.reduce((sum, page) => sum + (page?.jobs?.length ?? 0), 0);
				const totalCount = allPages[0]?.totalCount ?? 0;
				return loaded < totalCount ? allPages.length + 1 : undefined;
			},
			keepPreviousData: true,
			...opts,
		}
	);

	const seen = new Set<string>();
	const jobs = (infinite.data?.pages ?? [])
		.flatMap((page) => page?.jobs ?? [])
		.filter((job) => !!job.id && !seen.has(job.id) && !!seen.add(job.id));

	const totalCount = infinite.data?.pages[0]?.totalCount ?? 0;

	return { jobs, totalCount, ...infinite };
}

export function useJobSchedule(
	startTime: string,
	endTime: string,
	customerIds?: string[],
	contractIds?: string[],
	expand?: string[],
	include?: string[],
	enable: boolean = true,
	consultantManagerId?: string
) {
	if (!customerIds || !enable) {
		return { jobSchedules: [], isLoading: false };
	}

	const { data, ...res } = useQuery(
		[
			"useJobSchedule",
			startTime,
			endTime,
			enable,
			JSON.stringify(customerIds),
			JSON.stringify(contractIds),
			JSON.stringify(expand),
			JSON.stringify(include),
			consultantManagerId,
		],
		() =>
			apiStore
				.get<JobScheduleResponse>({
					path: "/schedule/job",
					isAuthRequired: true,
					query: {
						startTime,
						endTime,
						customerIds: customerIds ? customerIds.join(",") : "",
						contractIds: contractIds ? contractIds.join(",") : "",
						expand: expand ? expand.join(",") : "",
						include: include ? include.join(",") : "",
						consultantManagerId: consultantManagerId || undefined,
					},
				})
				.then((d) => d.data)
	);
	return { ...data, ...res };
}

export type GetJobParams = {
	jobId?: string;
	expand?: string[];
};

export function useJob({ jobId, expand }: GetJobParams, { enabled, ...opts }: QueryOpts = {}) {
	const { data, ...rest } = useQuery(
		["jobs", jobId, expand],
		() =>
			apiStore
				.get<Job>({
					path: "/job/" + jobId,
					isAuthRequired: true,
					query: {
						expand: expand ? expand.join(",") : undefined,
					},
				})
				.then((d) => d.data),
		{
			enabled: !!jobId && isUndefinedOrTrue(enabled),
			...opts,
		}
	);

	return {
		job: data,
		...rest,
	};
}

/**
 * The unified event timeline for a job (job history + time report
 * milestones), newest first. Fetched by the "Händelser" tab on activation.
 */
export function useJobHistory({ jobId }: { jobId?: string }, opts: QueryOpts = {}) {
	const { data, ...rest } = useQuery(
		["jobs", jobId, "history"],
		() =>
			apiStore
				.get<{ entries: JobHistoryEntry[] }>({
					path: `/job/${jobId}/history`,
					isAuthRequired: true,
				})
				.then((d) => d.data.entries),
		{
			enabled: !!jobId && isUndefinedOrTrue(opts.enabled),
			...opts,
		}
	);

	return {
		entries: data,
		...rest,
	};
}

type GetJobsShiftsParams = {
	jobId: string;
	status?: string[];
	include?: "applications"[];
};

export function useJobShifts(
	{ jobId, status, include }: GetJobsShiftsParams,
	opts: QueryOpts = {}
) {
	return useQuery(
		["jobs", jobId, "shifts", status, include],
		() =>
			apiStore
				.get<{ shifts: Shift[] }>({
					path: `/job/${jobId}/shift`,
					isAuthRequired: true,
					query: {
						status: status ? status.join(",") : undefined,
						include: include ? include.join(",") : undefined,
					},
				})
				.then((d) => d.data.shifts),
		{
			enabled: !!jobId && isUndefinedOrTrue(opts.enabled),
			...opts,
		}
	);
}

/**
 * Create a job. Toast-silent: the create page owns success navigation and
 * error feedback (the API error detail is shown to the user).
 */
export function useCreateJob() {
	const queryClient = useQueryClient();

	return useMutation<Job, ApiErrorResponse, CreateJobRequest>(
		(body) =>
			apiStore
				.post<Job>({
					path: `/job`,
					body,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(["jobs"]);
			},
		}
	);
}

/** Send a push/message to all consultants matching the job. */
export function useSendJobNotification() {
	return useMutation<unknown, ApiErrorResponse, { jobId: string; body: string }>((params) =>
		apiStore
			.post({
				path: `/job/${params.jobId}/send-notification-to-matches`,
				isAuthRequired: true,
				body: {
					title: "",
					body: params.body,
				},
			})
			.then((d) => d.data)
	);
}

export function useUpdateJob() {
	const queryClient = useQueryClient();

	return useMutation(
		(params: { jobId: string; update: Partial<Job>; silent?: boolean }) =>
			apiStore
				.put({
					path: `/job/${params.jobId}`,
					body: params.update,
					isAuthRequired: true,
				})
				.then((d) => d.data),

		{
			onSuccess: (_, data) => {
				if (!data.silent) {
					toast.success(FEEDBACK_MESSAGE_TYPE.CHANGES_SAVE_SUCCESSFULLY);
				}
				queryClient.invalidateQueries(["jobs"]);
			},
			onError: (_, data) => {
				if (!data.silent) {
					toast.error(FEEDBACK_MESSAGE_TYPE.SOMETHING_WENT_WRONG);
				}
			},
		}
	);
}

/**
 * Toggle a DESCRIPTIVE job tag (org `jobTagTypes`) on a job. Sends the full
 * next tag list via `PUT /job/:id` (`{ tags }` only — the backend validates the
 * keys against the org taxonomy). Optimistic: every job cache that holds the
 * job (detail entries, paged lists, the infinite list's pages) is patched in
 * onMutate so open checkboxes and list chips flip instantly, then rolled back
 * on error. Errors toast; success is silent (the UI already reflects it).
 */
export function useToggleJobTag() {
	const queryClient = useQueryClient();

	const patchJob = <T extends { id?: string }>(job: T, jobId: string, tags: { key: string }[]) =>
		job.id === jobId ? { ...job, tags } : job;

	const patchCaches = (jobId: string, tags: { key: string }[]) => {
		// Detail caches: ["jobs", jobId, expand]. The prefix also matches
		// sub-resource caches (history, shifts, applications…), so only patch
		// entries that are the job object itself.
		queryClient.setQueriesData<Job>(["jobs", jobId], (old) =>
			old && !Array.isArray(old) && (old as Job).id === jobId ? { ...old, tags } : old
		);

		// Paged lists (["jobs", query]) and the infinite list (["jobs", "infinite", query]).
		queryClient.setQueriesData<JobsResponse | { pages: JobsResponse[]; pageParams: unknown[] }>(
			["jobs"],
			(old) => {
				if (!old || typeof old !== "object") return old;
				if ("jobs" in old && Array.isArray(old.jobs)) {
					return { ...old, jobs: old.jobs.map((job) => patchJob(job, jobId, tags)) };
				}
				if ("pages" in old && Array.isArray(old.pages)) {
					return {
						...old,
						pages: old.pages.map((page) =>
							page && Array.isArray(page.jobs)
								? { ...page, jobs: page.jobs.map((job) => patchJob(job, jobId, tags)) }
								: page
						),
					};
				}
				return old;
			}
		);
	};

	return useMutation<
		Job,
		ApiErrorResponse,
		{ jobId: string; nextTags: { key: string }[] },
		{ entries: [QueryKey, unknown][] }
	>(
		(params) =>
			apiStore
				.put({
					path: `/job/${params.jobId}`,
					body: { tags: params.nextTags },
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onMutate: async ({ jobId, nextTags }) => {
				// Stop in-flight refetches from overwriting the optimistic patch.
				await queryClient.cancelQueries(["jobs"]);

				// Snapshot every cache we are about to patch, for rollback on error.
				const entries: [QueryKey, unknown][] = queryClient.getQueriesData(["jobs"]);

				patchCaches(jobId, nextTags);

				return { entries };
			},
			onError: ({ error }, _params, context) => {
				context?.entries.forEach(([key, data]) => queryClient.setQueryData(key, data));
				toast.apiError(error?.code);
			},
			onSuccess: (updated, { jobId }) => {
				if (updated?.id) {
					patchCaches(jobId, updated.tags ?? []);
				}
			},
			onSettled: () => {
				// Mark stale without refetching mounted queries; reconciles lazily.
				queryClient.invalidateQueries({ queryKey: ["jobs"], refetchType: "none" });
			},
		}
	);
}

export function useBookConsultant() {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutation<
		any,
		ApiErrorResponse,
		{ jobId: string; consultantId: string; shiftIds?: string[]; silent?: boolean }
	>(
		(params) =>
			apiStore
				.post({
					path: `/job/${params.jobId}/book`,
					body: {
						consultant: { id: params.consultantId },
						// Explicit shift selection — omitted, the backend books every
						// available shift (one per time window)
						shifts: params.shiftIds?.length ? params.shiftIds.map((id) => ({ id })) : undefined,
					},
					isAuthRequired: true,
				})
				.then((d) => d.data),

		{
			onSuccess: (_, data) => {
				if (!data.silent) {
					toast.success(t("job.consultantWasBooked"));
				}
				queryClient.invalidateQueries(["jobs", data.jobId]);
			},
			onError: ({ error }, data) => {
				if (!data.silent) {
					toast.apiError(error.code);
				}
			},
		}
	);
}

export function useUpdateApplication() {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutation<
		any,
		ApiErrorResponse,
		{ jobId: string; applicationId: string; status: APPLICATION_STATUS; silent?: boolean }
	>(
		(params) =>
			apiStore
				.put({
					path: `/job/${params.jobId}/application/${params.applicationId}`,
					body: { status: params.status },
					isAuthRequired: true,
				})
				.then((d) => d.data),

		{
			onSuccess: (_, data) => {
				if (!data.silent && data.status === APPLICATION_STATUS.ACCEPTED) {
					toast.success(t("job.consultantWasBooked"));
				}
				queryClient.invalidateQueries(["jobs", data.jobId]);
			},
			onError: ({ error }, data) => {
				if (!data.silent) {
					toast.apiError(error.code);
				}
			},
		}
	);
}

type GetMatchingConsultantsParams = {
	jobId: string;
	branch?: string;
	service?: string;
	skills?: string;
	hasCustomerExperience?: string;
	isInvited?: string;
	keyword?: string;
	availability?: string;
	pageSize?: number;
	includeBookedConsultants?: boolean;
	includeConsultantsWithOverlappedJob?: string;
	includePendingApplications?: boolean;
	timeSlots?: string;
	expand?: string;
};

export function useMatchingConsultants(
	{ jobId, ...query }: GetMatchingConsultantsParams,
	opts: QueryOpts = {}
) {
	return useQuery(
		["jobs", jobId, "users", query],
		() =>
			apiStore
				.get<{ users: MatchingConsultant[] }>({
					path: `/job/${jobId}/user`,
					isAuthRequired: true,
					query: { ...query },
				})
				.then((d) => d.data.users),
		{
			enabled: !!jobId && isUndefinedOrTrue(opts.enabled),
			...opts,
			initialData: [],
		}
	);
}

export function useInviteConsultantToJob() {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutation<
		any,
		ApiErrorResponse,
		{ jobId: string; consultantId: string; silent?: boolean }
	>(
		(params) =>
			apiStore
				.post({
					path: `/job/${params.jobId}/invitation`,
					body: { consultant: { id: params.consultantId } },
					isAuthRequired: true,
				})
				.then((d) => d.data),

		{
			onSuccess: (_, data) => {
				if (!data.silent) {
					toast.success(t("job.invitationsSent"));
				}

				queryClient.invalidateQueries(["jobs", data.jobId, "applications"]);
			},
			onError: ({ error }, data) => {
				if (!data.silent) {
					toast.apiError(error.code);
				}
			},
		}
	);
}

export function useDeleteInvitation() {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutation<
		any,
		ApiErrorResponse,
		{ jobId: string; invitationId: string; silent?: boolean }
	>(
		(params) =>
			apiStore
				.delete({
					path: `/job/${params.jobId}/invitation/${params.invitationId}`,
					isAuthRequired: true,
				})
				.then((d) => d.data),

		{
			onSuccess: (_, data) => {
				if (!data.silent) {
					toast.success(t("job.invitationDeleted"));
				}

				queryClient.invalidateQueries(["jobs", data.jobId, "applications"]);
			},
			onError: ({ error }, data) => {
				if (!data.silent) {
					toast.apiError(error.code);
				}
			},
		}
	);
}

/**
 * Hard-deletes an application (advanced admin action) so the consultant can
 * re-apply — a declined application otherwise blocks a new one.
 */
export function useDeleteApplication() {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutation<any, ApiErrorResponse, { jobId: string; applicationId: string }>(
		(params) =>
			apiStore
				.delete({
					path: `/job/${params.jobId}/application/${params.applicationId}`,
					isAuthRequired: true,
				})
				.then((d) => d.data),

		{
			onSuccess: (_, data) => {
				toast.success(t("jobCandidates.applicationDeleted"));
				queryClient.invalidateQueries(["jobs", data.jobId]);
			},
			onError: ({ error }) => {
				toast.apiError(error.code);
			},
		}
	);
}

type GetApplicationsParams = {
	jobId: string;
	includeInvitations?: boolean;
};

export function useApplications({ jobId, ...query }: GetApplicationsParams, opts: QueryOpts = {}) {
	const { data, ...rest } = useQuery(
		["jobs", jobId, "applications", query],
		() =>
			apiStore
				.get<{
					applications: Application[];
					invitations?: Invitation[];
					/** What accepting a pending application would book right now: the
					 * job's still-unmanned shifts, one per overlapping time window
					 * (parallel shifts hold capacity for several consultants). The
					 * same for every pending application — only set when the job has
					 * pending applications. */
					wouldBookShifts?: { id: string; start: string; end: string }[];
				}>({
					path: `/job/${jobId}/application`,
					isAuthRequired: true,
					query: {
						include: query.includeInvitations ? "invitations" : undefined,
						expand: "consultant,metadata,consultant.hasCustomerExperience",
					},
				})
				.then((d) => d.data),
		{
			enabled: isUndefinedOrTrue(opts.enabled),
			...opts,
		}
	);

	return {
		applications: data?.applications,
		invitations: data?.invitations,
		wouldBookShifts: data?.wouldBookShifts,
		...rest,
	};
}

export function useUpdateApplicationStatus() {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutation<
		any,
		ApiErrorResponse,
		{
			jobId: string;
			applicationId: string;
			status: APPLICATION_STATUS;
			silent?: boolean;
			/** True when declining an already-booked application (unbook) vs denying a pending one. */
			wasBooked?: boolean;
		}
	>(
		(params) =>
			apiStore
				.put({
					path: `/job/${params.jobId}/application/${params.applicationId}`,
					body: { status: params.status },
					isAuthRequired: true,
				})
				.then((d) => d.data),

		{
			onSuccess: (_, data) => {
				if (!data.silent && data.status === APPLICATION_STATUS.DECLINED) {
					toast.success(
						t(data.wasBooked ? "job.consultantWasUnbooked" : "job.applicationWasDeclined")
					);
				}

				// Invalidate everything with this job as some computed values might be
				// updated after application is changed
				queryClient.invalidateQueries(["jobs", data.jobId]);
			},
			onError: ({ error }, data) => {
				if (!data.silent) {
					toast.apiError(error.code);
				}
			},
		}
	);
}

export function useDenyJob() {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutation<
		any,
		ApiErrorResponse,
		{ jobId: string; reasonKey?: string; reasonText?: string; silent?: boolean }
	>(
		(params) =>
			apiStore
				.post({
					path: `/job/${params.jobId}/deny`,
					isAuthRequired: true,
					body: {
						...(params.reasonKey ? { reasonKey: params.reasonKey } : {}),
						...(params.reasonText ? { reasonText: params.reasonText } : {}),
					},
				})
				.then((d) => d.data),

		{
			onSuccess: (_, data) => {
				toast.success(t("job.denied"));
				queryClient.invalidateQueries(["jobs", data.jobId]);
			},
			onError: ({ error }, data) => {
				if (!data.silent) {
					toast.apiError(error.code);
				}
			},
		}
	);
}

export function useCancelJob() {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutation<any, ApiErrorResponse, { jobId: string; silent?: boolean }>(
		(params) =>
			apiStore
				.post({
					path: `/job/${params.jobId}/cancel`,
					isAuthRequired: true,
				})
				.then((d) => d.data),

		{
			onSuccess: (_, data) => {
				if (!data.silent) {
					toast.success(t("job.cancelled"));
				}
				queryClient.invalidateQueries(["jobs", data.jobId]);
			},
			onError: ({ error }, data) => {
				if (!data.silent) {
					toast.apiError(error.code);
				}
			},
		}
	);
}

export function useDeleteJob() {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutation<
		any,
		ApiErrorResponse,
		{ jobId: string; silent?: boolean; onSuccess?: () => any }
	>(
		(params) =>
			apiStore
				.delete({
					path: `/job/${params.jobId}`,
					isAuthRequired: true,
				})
				.then((d) => d.data),

		{
			onSuccess: (_, data) => {
				if (!data.silent) {
					toast.success(t("job.deleted"));
				}
				queryClient.invalidateQueries(["jobs", data.jobId]);

				data.onSuccess && data.onSuccess();
			},
			onError: ({ error }, data) => {
				if (!data.silent) {
					toast.apiError(error.code);
				}
			},
		}
	);
}

export function useReopenJob() {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutation<any, ApiErrorResponse, { jobId: string; silent?: boolean }>(
		(params) =>
			apiStore
				.post({
					path: `/job/${params.jobId}/reopen`,
					isAuthRequired: true,
				})
				.then((d) => d.data),

		{
			onSuccess: (_, data) => {
				if (!data.silent) {
					toast.success(t("job.reopened"));
				}
				queryClient.invalidateQueries(["jobs", data.jobId]);
			},
			onError: ({ error }, data) => {
				if (!data.silent) {
					toast.apiError(error.code);
				}
			},
		}
	);
}

export interface UpdateShiftRequestRow {
	id?: string;
	start?: string;
	end?: string;
	invite?: string[] | null;
	consultant?: string | null;
	break?: number;
	/** Planner row index for parallel unassigned shifts (presentation-only). */
	lane?: number;
	// cancel?: boolean;
}

type WithRequired<T, K extends keyof T> = T & { [P in K]-?: T[P] };

export type ShiftError = {
	code: ErrorCode;
	detail: string;
};

export enum ErrorCode {
	HAS_INVOICED_SHIFT = "HAS_INVOICED_SHIFT",
	SHIFT_OVERLAPPED = "SHIFT_OVERLAPPED",
	ACCEPTED_CONSULTANT_SHIFT_OVERLAP = "ACCEPTED_CONSULTANT_SHIFT_OVERLAP",
	CONSULTANT_NOT_AVAILABLE = "CONSULTANT_NOT_AVAILABLE",
	SCHEDULE_CONFLICT = "SCHEDULE_CONFLICT",
	MISSING_POLICE_EXTRACT = "MISSING_POLICE_EXTRACT",
}

export interface UpdateShiftResponseRow
	extends WithRequired<UpdateShiftRequestRow, "id" | "start" | "end"> {
	errors?: ShiftError[];
}

export function useUpdateShifts() {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutation(
		(params: { jobId: string; shifts: UpdateShiftRequestRow[] }) =>
			apiStore
				.put<{ shifts: UpdateShiftResponseRow[] }>({
					path: `/job/${params.jobId}/shifts`,
					isAuthRequired: true,
					body: {
						shifts: params.shifts,
					},
				})
				.then((d) => d.data),

		{
			onSuccess: (_, data) => {
				//queryClient.invalidateQueries(["jobs", data.jobId, "shifts"]);
				queryClient.invalidateQueries(["jobs"]);
			},
			onError: ({ error }) => {
				const msg = t(`error.${error.code}`, JSON.stringify(error));
				toast.error(msg);
			},
		}
	);
}
export function useScheduleSummaryData(
	startTime: string,
	endTime: string,
	customerIds?: string[],
	contractIds?: string[],
	consultantIds?: string[],
	pools?: string[]
) {
	const { data, ...res } = useQuery(
		[
			"schedule-summary-data",
			startTime,
			endTime,
			JSON.stringify(customerIds),
			JSON.stringify(contractIds),
			JSON.stringify(consultantIds),
			JSON.stringify(pools),
		],
		() =>
			apiStore
				.get<ScheduleTooltipData>({
					path: "/schedule/job/count",
					isAuthRequired: true,
					query: {
						startTime,
						endTime,
						customerIds: customerIds ? customerIds.join(",") : "",
						contractIds: contractIds ? contractIds.join(",") : "",
						consultantIds: consultantIds ? consultantIds.join(",") : "",
						pools: pools ? pools.join(",") : "",
					},
				})
				.then((d) => d.data)
	);
	return { ...data, ...res };
}
